import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Typography,
  Box,
  Button,
  MobileStepper,
  IconButton,
  useTheme
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';

interface DemoTourDialogProps {
  open: boolean;
  onClose: () => void;
}

const TOUR_STEPS = [
  {
    title: 'Your Portfolio Hub',
    description:
      'Get an immediate, high-level look at your total portfolio valuation, cost basis metrics, and net capital gains. Dynamic asset allocation and sector breakdown charts visualize your market diversification instantly.',
    imagePath: '/static/images/tour/dashboard.png'
  },
  {
    title: 'Automated Broker Ingestion',
    description:
      'Eliminate manual error and tedious data entry. Select your trading broker and drag-and-drop your native trading CSV data files to securely parse, run, and sync transaction logs directly into your ledger.',
    imagePath: '/static/images/tour/import.png'
  },
  {
    title: 'Manual Adjustments & Historical Patches',
    description:
      'Need to account for a legacy asset or plug a gap from a previous financial year? Easily backdate missed transactions, add off-market transfers, and fine-tune historical data points to keep your lifetime tracking pristine.',
    imagePath: '/static/images/tour/add_missed_trans.png'
  },
  {
    title: 'Live Holdings Tracking',
    description:
      'Monitor open positions with precise accounting control. Inspect purchase costs, closing prices, total profits, and net performance swings across your asset classes with zero-quantity toggles.',
    imagePath: '/static/images/tour/holdings.png'
  },
  {
    title: 'Seamless Corporate Adjustments',
    description:
      'Manage complex corporate transitions gracefully. Process share exchanges, historical acquisitions, stock mergers, or unit re-scalings natively to keep historical cost bases accurate over long horizons.',
    imagePath: '/static/images/tour/corporate_actions.png'
  },
  {
    title: 'Automated Fiscal Tax Reporting',
    description:
      'Simplify your end-of-financial-year workflows. Review clear, chronological breakdowns of realized Short Term Gains (STG) and Long Term Gains (LTG) with full cost-basis matching and downloadable Excel export tools.',
    imagePath: '/static/images/tour/tax_reports.png'
  }
];

const DemoTourDialog = ({ open, onClose }: DemoTourDialogProps) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = TOUR_STEPS.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const currentStep = TOUR_STEPS[activeStep];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, position: 'relative' }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.3rem',
          color: 'primary.main',
          borderBottom: '1px solid #eef2f6',
          pb: 2,
          pr: 6
        }}
      >
        Platform Walkthrough
      </DialogTitle>

      {/* Quick Close Corner Cross button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pt: 3 }}>
        <Card
          sx={{
            height: 360, // Bumped slightly so dashboard details are easier to read
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px solid #eef2f6',
            backgroundColor: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* FIXED: Now using the correct path from your array */}
          <Box
            component="img"
            src={currentStep.imagePath}
            alt={currentStep.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain', // 'contain' ensures your wide app dashboards don't get awkwardly cropped
              backgroundColor: '#f8f9fa'
            }}
          />
        </Card>

        {/* Dynamic Context Description Section */}
        <Box sx={{ my: 3, px: 1, minHeight: 70 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {currentStep.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {currentStep.description}
          </Typography>
        </Box>
      </DialogContent>

      {/* Stepper Interactive Command Layer */}
      <DialogActions
        sx={{ p: 2, borderTop: '1px solid #eef2f6', display: 'block' }}
      >
        <MobileStepper
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          sx={{ background: 'transparent', p: 0 }}
          nextButton={
            activeStep === maxSteps - 1 ? (
              <Button
                size="small"
                variant="contained"
                onClick={onClose}
                disableElevation
              >
                Get Started
              </Button>
            ) : (
              <Button
                size="small"
                onClick={handleNext}
                endIcon={<KeyboardArrowRight />}
              >
                Next
              </Button>
            )
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<KeyboardArrowLeft />}
            >
              Back
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default DemoTourDialog;
